import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Award, Clock, Shield, MessageCircle, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ContactModal from '../components/ContactModal';
import ResponsiveCarousel from '../components/ResponsiveCarousel';
import LandingPageFooter from '../components/LandingPageFooter';
import { stats, pricingPackages, testimonials, faqs, whyChooseUsPoints } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

// Process Steps Carousel Component
const ProcessCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );
  
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const steps = [
    {
      number: 1,
      title: 'Submit Your Details',
      description: 'Provide us with your brand name, logo, and basic business information through our secure online form.',
      icon: '📝'
    },
    {
      number: 2,
      title: 'We Handle the Filing',
      description: 'Our experts conduct a trademark search, prepare your application, and file it with the USPTO — accurately and efficiently.',
      icon: '⚡'
    },
    {
      number: 3,
      title: 'Get Official Protection',
      description: 'Once approved, your trademark is officially registered, giving you exclusive legal rights and nationwide brand protection.',
      icon: '🛡️'
    }
  ];

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {steps.map((step, index) => (
            <div key={index} className="flex-[0_0_100%] md:flex-[0_0_33.333%] min-w-0">
              <div className="bg-white rounded-xl shadow-md p-6 border-2 border-[#FF7F7F]/20 hover:border-[#FF7F7F] transition-all h-full">
                <div className="w-12 h-12 bg-[#FF7F7F] text-white rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto">
                  {step.number}
                </div>
                <div className="text-3xl text-center mb-3">{step.icon}</div>
                <h3 className="text-lg font-bold text-[#4B0082] mb-3 text-center">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-center text-sm">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="flex justify-center mt-6 gap-2">
        {steps.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all ${
              index === selectedIndex 
                ? 'bg-[#FF7F7F] w-6' 
                : 'bg-gray-300'
            }`}
            onClick={() => emblaApi && emblaApi.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

// Testimonials Carousel Component
const TestimonialsCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );
  
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0">
              <Card className="h-full border-2 border-gray-200 hover:border-[#FF7F7F] transition-all">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-[#4B0082]">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#4B0082] hover:bg-[#FF7F7F] hover:text-white transition-all z-10"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center mt-6 gap-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all ${
              index === selectedIndex 
                ? 'bg-[#FF7F7F] w-6' 
                : 'bg-gray-300'
            }`}
            onClick={() => emblaApi && emblaApi.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const trademarkPricing = pricingPackages['trademark-registration'] || [];
  const trademarkFaqs = faqs['trademark-registration'] || [];

  const handleRegisterClick = () => {
    setIsModalOpen(true);
  };

  const handleChatNow = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>$45 Trademark Registration - Fast & Reliable | Trademark Frontline</title>
        <meta name="description" content="Register your trademark for just $45! Expert filing with 98% approval rate. Secure your brand today with Trademark Frontline." />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Custom Header - No Navigation */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Award className="h-8 w-8 text-[#FF7F7F]" />
              <span className="ml-2 text-2xl font-bold text-[#4B0082]">Trademark Frontline</span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-4">
              <Button
                className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white hidden md:inline-flex"
                onClick={handleRegisterClick}
              >
                Register My Trademark
              </Button>
              <Button
                variant="outline"
                className="border-[#4B0082] text-[#4B0082] hover:bg-[#4B0082] hover:text-white"
                onClick={handleChatNow}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Chat Now
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-[#4B0082]/5 to-[#FF7F7F]/10 py-12 md:py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-[#FF7F7F] rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-[#4B0082] rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-[#E6E6FA] rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Form First on Mobile, Content on Desktop */}
            <div className="order-2 lg:order-1 space-y-6">
              <div className="inline-block bg-[#FF7F7F]/10 text-[#FF7F7F] px-4 py-2 rounded-full text-sm font-semibold">
                Ready to Protect Your Brand?
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#4B0082] leading-tight">
                We Make U.S. Trademark Registration Stress-Free
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                Secure your business name, logo, or slogan with Trademark Frontline — your reliable partner for fast and accurate trademark filings. Our experts guide you every step of the way with professional support.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
                  onClick={handleRegisterClick}
                >
                  Register My Trademark
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#4B0082] text-[#4B0082] hover:bg-[#4B0082] hover:text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
                  onClick={handleChatNow}
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Live Chat
                </Button>
              </div>
            </div>

            {/* Form First on Mobile (order-1), Right on Desktop (order-2) */}
            <div className="order-1 lg:order-2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border-2 border-[#FF7F7F]/20" id="contact-form">
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-[#4B0082] mb-2">
                  Protect Your Brand Today
                </h3>
                <p className="text-sm sm:text-base text-[#FF7F7F] font-semibold">
                  Starting at Just $45 + Free Consultation
                </p>
              </div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 sm:py-12 bg-white border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-1 sm:mb-2">{stats.approvalRate}</div>
              <div className="text-xs sm:text-sm text-gray-600">Approval Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-1 sm:mb-2">{stats.support}</div>
              <div className="text-xs sm:text-sm text-gray-600">Customer Support</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-1 sm:mb-2">{stats.trademarksFiled}</div>
              <div className="text-xs sm:text-sm text-gray-600">Trademarks Filed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-1 sm:mb-2">{stats.statesCovered}</div>
              <div className="text-xs sm:text-sm text-gray-600">States Covered</div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#FF7F7F] font-semibold text-xs sm:text-sm mb-2">WHAT WE DO</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-3 sm:mb-4 px-4">
              Protect Your Brand's Identity Through Trademark Registration
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto px-4">
              At Trademark Frontline, we specialize in protecting your brand through fast, affordable, and reliable trademark registration services across the USA. We simplify the legal process so you can focus on growing your business with peace of mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Image First on Mobile */}
            <div className="order-1 md:order-2 relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
                alt="Trademark Registration and Business Documents"
                className="w-full h-[300px] sm:h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4B0082]/60 to-transparent"></div>
            </div>

            {/* Content After Image on Mobile */}
            <div className="order-2 md:order-1 space-y-4 sm:space-y-6">
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF7F7F] mr-2 sm:mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#4B0082] mb-1 text-sm sm:text-base">Trademark Search & Clearance</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Comprehensive search to ensure your mark is available</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF7F7F] mr-2 sm:mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#4B0082] mb-1 text-sm sm:text-base">USPTO Trademark Filing</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Expert preparation and filing of your trademark application</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF7F7F] mr-2 sm:mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#4B0082] mb-1 text-sm sm:text-base">Office Action Responses</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Professional handling of any USPTO objections or questions</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF7F7F] mr-2 sm:mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#4B0082] mb-1 text-sm sm:text-base">Trademark Renewals</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Timely reminders and assistance with trademark maintenance</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 text-lg max-w-3xl mx-auto mb-6">
              Whether you're just starting out or expanding your business, Trademark Frontline is your dedicated partner in brand protection. We offer clear communication, expert guidance, and a hassle-free experience every step of the way.
            </p>
            <Button
              size="lg"
              className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white"
              onClick={handleRegisterClick}
            >
              Register My Trademark
            </Button>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#FF7F7F] font-semibold text-xs sm:text-sm mb-2">SIMPLE PROCESS STEPS</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-3 sm:mb-4 px-4">
              Three Steps To Protect Your Brand's Identity
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto px-4">
              Getting your trademark registered has never been easier. At Trademark Frontline, we've developed a seamless and efficient three-step process designed to eliminate confusion, reduce delays, and save you valuable time.
            </p>
          </div>

          <ProcessCarousel />

          <div className="mt-12 text-center">
            <Button
              size="lg"
              className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white mr-4"
              onClick={handleRegisterClick}
            >
              Register My Trademark
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-[#4B0082] text-[#4B0082] hover:bg-[#4B0082] hover:text-white"
              onClick={handleChatNow}
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Live Chat
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#FF7F7F] font-semibold text-xs sm:text-sm mb-2">OUR PRICING</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-3 sm:mb-4 px-4">
              Pick Your Trademark Package!
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto px-4">
              Choose the package that best fits your needs. All packages include expert support and guidance throughout the process.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 max-w-7xl mx-auto">
            {trademarkPricing.map((pkg, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden transition-all duration-300 flex flex-col w-full md:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] ${
                  pkg.popular
                    ? 'border-4 border-[#FF7F7F] shadow-2xl transform scale-105'
                    : 'border-2 border-gray-200 hover:border-[#FF7F7F] hover:shadow-xl'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-[#FF7F7F] text-white px-4 py-1 text-sm font-semibold">
                    POPULAR
                  </div>
                )}
                <CardContent className="p-6 flex flex-col flex-grow">
                  <h3 className="text-2xl font-bold text-[#4B0082] mb-2">{pkg.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 h-12">{pkg.description}</p>
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-5xl font-bold text-[#4B0082]">${pkg.price}</span>
                      {pkg.originalPrice && (
                        <span className="text-2xl text-gray-400 line-through ml-2">${pkg.originalPrice}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">+USPTO fees</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto mb-6 space-y-2 pr-2 flex-grow" style={{ scrollbarWidth: 'thin', scrollbarColor: '#FF7F7F #f1f1f1' }}>
                    {pkg.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-[#FF7F7F] mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    className={`w-full mt-auto ${
                      pkg.popular
                        ? 'bg-[#FF7F7F] hover:bg-[#FF6B6B]'
                        : 'bg-[#4B0082] hover:bg-[#3A0062]'
                    } text-white transition-all`}
                    onClick={handleRegisterClick}
                  >
                    Register My Trademark
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#FF7F7F] font-semibold text-xs sm:text-sm mb-2">WHY CHOOSE US</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-3 sm:mb-4 px-4">
              Your Trusted Brand Protection Partner
            </h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto px-4">
              Whether you're just starting out or expanding your business, Trademark Frontline is your dedicated partner in brand protection. We offer clear communication, expert guidance, and a hassle-free experience every step of the way.
            </p>
          </div>

          <ResponsiveCarousel
            items={whyChooseUsPoints}
            renderItem={(point, index) => {
              const icons = [
                <Award className="h-10 w-10 text-[#FF7F7F]" />,
                <Shield className="h-10 w-10 text-[#FF7F7F]" />,
                <Clock className="h-10 w-10 text-[#FF7F7F]" />
              ];
              
              return (
                <Card className="group relative overflow-hidden border-2 border-gray-200 hover:border-[#FF7F7F] transition-all duration-500 h-full hover:shadow-2xl hover:-translate-y-2">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FF7F7F]/0 to-[#4B0082]/0 group-hover:from-[#FF7F7F]/5 group-hover:to-[#4B0082]/5 transition-all duration-500 pointer-events-none"></div>
                  
                  <CardContent className="p-8 relative z-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#FF7F7F]/10 to-[#4B0082]/10 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-lg transition-all duration-500 group-hover:from-[#FF7F7F]/20 group-hover:to-[#4B0082]/20">
                      <div className="group-hover:scale-110 transition-transform duration-500">
                        {icons[index]}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-[#4B0082] mb-4 text-center group-hover:text-[#FF7F7F] transition-colors duration-300">{point.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center group-hover:text-gray-700 transition-colors duration-300">{point.description}</p>
                  </CardContent>
                  
                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF7F7F] to-[#4B0082] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </Card>
              );
            }}
            gridCols="md:grid-cols-3"
            gap="gap-8"
          />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#FF7F7F] font-semibold text-xs sm:text-sm mb-2">TESTIMONIALS</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-3 sm:mb-4 px-4">What Our Client Says</h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto px-4">
              Trusted by businesses nationwide for reliable trademark protection.
            </p>
          </div>

          <TestimonialsCarousel />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-[#FF7F7F] font-semibold text-xs sm:text-sm mb-2">FAQs</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#4B0082] mb-3 sm:mb-4 px-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-base sm:text-lg px-4">
              Got questions? Find quick answers to common trademark concerns here.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {trademarkFaqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg border-2 border-gray-200 px-6">
                <AccordionTrigger className="text-left text-[#4B0082] font-semibold hover:text-[#FF7F7F] transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-br from-[#4B0082] to-[#3A0062] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-[#FF7F7F] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF7F7F] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            Don't Risk Your Brand's Future!
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-6 sm:mb-8 leading-relaxed">
            Unregistered trademarks can lead to costly legal battles. Protect your brand with Trademark Frontline's expert services—fast, reliable, and hassle-free. Secure your exclusive rights now before it's too late!
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <Button
              size="lg"
              className="bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
              onClick={handleRegisterClick}
            >
              Register My Trademark
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#4B0082] px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
              onClick={handleChatNow}
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <LandingPageFooter />

      {/* Contact Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
    </>
  );
};

export default LandingPage;
